<script lang="ts">
    import {SvelteDate, SvelteMap} from "svelte/reactivity";
    import SchedulerToolbar from "./svelte/SchedulerToolbar.svelte";
    import {buildVehicles} from "./init.svelte.js";
    import {
        zoomRanges,
        getZoomWithBlocks,
        type ZoomRangeWithBlocks,
    } from "./zoom2.svelte.js";
    import {getOverlappingJobs, type OverlappingJobs} from "./overlappingJobs.svelte.js";
    import type {
        SchedularVehicle,
        SetView,
        RelatedJobMap,
        SchedularVehicleAssignment, SchedularEmployee, SchedularEmployeeAssignment,
        SiteVisitMap
    } from './types';
    import {
        isStateJob,
        isStateScheduledJob,
        type PageStateAny,
    } from "./state";
    import Scheduler from "./scheduler.svelte";
    import Loading from "../../svelte/loading.svelte";
    import {saveSvelte} from "./save.svelte";
    import Modal from "@/components/Modal.svelte";
    import Validation from "./svelte/validation.svelte";
    import { Button } from "@/components/ui/button";
    import {validateJobs, type ValidationStoreInner} from "./validate";
    import SearchField from "./svelte/searchField.svelte";
    import {fetchScheduleable} from "./fetch.svelte";
    import {onMount, onDestroy, tick} from "svelte";
    import SelectedInfo from "./svelte/selectedInfo.svelte";
    import {getModifiedJobsCount} from "./lib";
    import {
        onMouseDownLeftDurJob,
        onMouseDownPosJob,
        onMouseDownRightDurJob,
        onMouseUpJob,
        onMouseMoveJob,
    } from "./mouseJobs.svelte";
    import type { Field } from "@/types/types";
    import {
        createDebouncedAutosave,
        performAutosave,
        getInvalidIds,
        type AutosaveStatus,
    } from "./autosave.svelte";
    import { showServerErrorOverlay } from "@/lib/errorHandler.svelte";
    import { router } from '@inertiajs/svelte';
    import * as AlertDialog from '@/components/ui/alert-dialog';

    import EmployeeTile from "@/components/scheduler/svelte/EmployeeTile.svelte";
    import SchedulerLegend from "@/components/scheduler/svelte/SchedulerLegend.svelte";
    import SchedulerDateField from "@/components/scheduler/svelte/schedulerDateField.svelte";
    import { ChevronLeft, ChevronsLeft, ChevronRight, ChevronsRight, Maximize, Minimize } from "lucide-svelte";

    let isFullscreen = $state(false);
    $effect(() => {
        isFullscreen; // reactive dependency
        updateContainerHeight?.();
    });

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            topNode?.requestFullscreen().catch(() => {
                // Fallback: CSS-only fullscreen if API unavailable
                isFullscreen = !isFullscreen;
            });
        } else {
            document.exitFullscreen();
        }
    };

    const onFullscreenChange = () => {
        isFullscreen = !!document.fullscreenElement;
    };

    let debouncedFetchTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedFetchScheduleable = (args: Parameters<typeof fetchScheduleable>[0]) => {
        if (debouncedFetchTimer) clearTimeout(debouncedFetchTimer);
        debouncedFetchTimer = setTimeout(() => fetchScheduleable(args), 1000);
    };

    // Background polling for new events. Picks up VAs/site visits/jobs created
    // by other users without forcing a page refresh. The underlying
    // fetchScheduleable() skips items already present in the local maps, so
    // unsaved local edits are never overwritten by a poll.
    const POLL_INTERVAL_MS = 60_000;
    let pollIntervalId: ReturnType<typeof setInterval> | null = null;

    type ViewMode = 'day' | 'week' | 'month';
    let viewMode: ViewMode = $state('week');

    const VIEW_SCROLL_LEVELS: Record<ViewMode, number> = {
        day: 1805,
        week: 3610,
        month: 3839,
    };

    const snapDisplayDate = (date: SvelteDate, mode: ViewMode): SvelteDate => {
        const d = new SvelteDate(date);
        if (mode === 'week') {
            const day = d.getDay();
            const diff = (day === 0 ? -6 : 1 - day);
            d.setDate(d.getDate() + diff);
            d.setHours(0, 0, 0, 0);
        } else if (mode === 'month') {
            d.setDate(1);
            d.setHours(0, 0, 0, 0);
        }
        return d;
    };

    const setViewMode = (mode: ViewMode) => {
        viewMode = mode;
        const snapped = snapDisplayDate(view.displayDate, mode);
        setView({
            displayDate: snapped,
            currentZoomStore: getZoomWithBlocks(VIEW_SCROLL_LEVELS[mode], zoomRanges, snapped, totalPxToDisplay) ?? view.currentZoomStore,
            refetchJobs: false,
            scrollLeft: 0,
        });
    };

    const navStep = (direction: 1 | -1, big: boolean) => {
        const d = new SvelteDate(view.displayDate);
        if (viewMode === 'day') {
            if (big) {
                d.setDate(d.getDate() + direction);
            } else {
                d.setHours(d.getHours() + direction);
            }
            setView({ displayDate: d, refetchJobs: false });
        } else if (viewMode === 'week') {
            if (big) {
                d.setDate(d.getDate() + direction * 7);
                const snapped = snapDisplayDate(d, 'week');
                setView({ displayDate: snapped, currentZoomStore: getZoomWithBlocks(VIEW_SCROLL_LEVELS.week, zoomRanges, snapped, totalPxToDisplay) ?? view.currentZoomStore, refetchJobs: false, scrollLeft: 0 });
            } else {
                d.setDate(d.getDate() + direction);
                setView({ displayDate: d, refetchJobs: false });
            }
        } else {
            if (big) {
                d.setMonth(d.getMonth() + direction, 1);
                d.setHours(0, 0, 0, 0);
                setView({ displayDate: d, currentZoomStore: getZoomWithBlocks(VIEW_SCROLL_LEVELS.month, zoomRanges, d, totalPxToDisplay) ?? view.currentZoomStore, refetchJobs: false, scrollLeft: 0 });
            } else {
                d.setDate(d.getDate() + direction * 7);
                setView({ displayDate: d, refetchJobs: false });
            }
        }
    };

    const goToNow = () => {
        const now = new SvelteDate();
        const snapped = snapDisplayDate(now, viewMode);
        setView({
            displayDate: snapped,
            currentZoomStore: getZoomWithBlocks(VIEW_SCROLL_LEVELS[viewMode], zoomRanges, snapped, totalPxToDisplay) ?? view.currentZoomStore,
            scrollLeft: 0,
            refetchJobs: false,
        });
    };

    let dateRangeLabel = $derived.by(() => {
        const d = view.displayDate;
        if (viewMode === 'day') {
            return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        } else if (viewMode === 'week') {
            const mon = snapDisplayDate(new SvelteDate(d), 'week');
            const sun = new SvelteDate(mon);
            sun.setDate(sun.getDate() + 6);
            const monStr = mon.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
            const sunStr = sun.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            return `${monStr} – ${sunStr}`;
        } else {
            return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        }
    });

    type Props = {
        baseUrl: string;
        csrfToken: string;
        schedularVehicles: SchedularVehicle[];
        readonlyPlanner: boolean;
        startingDate: SvelteDate;
        endingDate: SvelteDate;
        initialDisplayDate?: SvelteDate;
        highlightedAssignmentId?: string | null;
        jobTypeField?: Field | null;
        purposeField?: Field | null;
    };

    let {baseUrl, csrfToken, schedularVehicles, readonlyPlanner, startingDate, endingDate, initialDisplayDate, highlightedAssignmentId, jobTypeField = null, purposeField = null}: Props = $props();
    type VehicleAssignmentMap = SvelteMap<string, SchedularVehicleAssignment>;
    const vehicleAssignmentMap: VehicleAssignmentMap = new SvelteMap();

    const siteVisitMap: SiteVisitMap = new SvelteMap();

    type EmployeeMap = SvelteMap<string, SchedularEmployee>;
    const employeesMap: EmployeeMap = new SvelteMap();

    type OverlappingMap = SvelteMap<string, {employee_id: string, assignment1: SchedularEmployeeAssignment, assignment2: SchedularEmployeeAssignment}>;
    const overlappingEmployeeAssignments: OverlappingMap = new SvelteMap();

    // console.log({startingDate, endingDate});

    let loading: string[] = $state([]);

    const fetchedJobsRange = {
        start: new SvelteDate(),
        end: new SvelteDate(),
    };
    fetchedJobsRange.start.setMonth(new Date().getMonth() - 1);
    fetchedJobsRange.end.setMonth(new Date().getMonth() + 1);
    let schedularVehiclesData = schedularVehicles.data ?? [];

    let vehicles = $state(buildVehicles(schedularVehiclesData));
    const relatedJobsMap: RelatedJobMap = new SvelteMap();

    fetchScheduleable({
        csrfToken,
        loading,
        vehicleAssignmentMap,
        siteVisitMap,
        start: fetchedJobsRange.start,
        end: fetchedJobsRange.end,
        vehicles,
        relatedJobsMap,
    });

    let topNode: HTMLDivElement;

    const displayDate = initialDisplayDate ?? new SvelteDate();
    // displayDate.setHours(displayDate.getHours() - 1);
    displayDate.setMinutes(0);
    displayDate.setSeconds(0);
    displayDate.setMilliseconds(0);

    // Defaults
    let rowStepPx = 70;
    let dragTargetDiv: HTMLDivElement;
    let contentDiv: HTMLDivElement;
    let usersWidth = 150;
    let totalPxToDisplay = 8_000;

    /**
     * This should not be mutated directly, it follows the type View but it is mutable.
     */
    const view: {
        displayDate: SvelteDate;
        currentZoomStore: ZoomRangeWithBlocks;
        startingDate: SvelteDate;
        endingDate: SvelteDate;
    } = $state({
        displayDate,
        currentZoomStore: getZoomWithBlocks(
            VIEW_SCROLL_LEVELS.week,
            zoomRanges,
            displayDate,
            totalPxToDisplay,
        )!,
        startingDate: startingDate,
        endingDate: endingDate,
    });

    const pageState = $state({
        searchStr: "",
        displayPopup: false,
        selected: null,
    }) as PageStateAny;

    let selectedInfoVisible = $state(false);

    $effect(() => {
        if (!isStateJob(pageState)) selectedInfoVisible = false;
    });

    let selectedVisibility = $derived(
        isStateJob(pageState) && selectedInfoVisible ? "grid" : "none",
    );

    let selectedOpacity = $derived(
        (isStateJob(pageState) && pageState.selected.dragging) ? 0.5 : 1,
    );

    const overlappingJobs = $derived(getOverlappingJobs(vehicleAssignmentMap));

    let validationStore: ValidationStoreInner = $derived(
        validateJobs(vehicleAssignmentMap, overlappingJobs),
    );

    let displayValidation = $state(false);

    let modifiedCount = $derived(getModifiedJobsCount(vehicleAssignmentMap, siteVisitMap));

    const setView: SetView = ({
                                  displayDate = view.displayDate,
                                  scrollLeft = 0,
                                  currentZoomStore = view.currentZoomStore,
                                  refetchJobs = true,
                                  smoothScroll = false,
                              }) => {
        // Set the scrollLeft to 0 and add the offset to the displayDate
        if (scrollLeft != 0) {
            const dateDiff =
                (scrollLeft * currentZoomStore.zoomRangePreset.cellMs) /
                currentZoomStore.cellPx;
            const newDateMs = displayDate.getTime() - dateDiff;
            return setView({
                displayDate: new SvelteDate(newDateMs),
                scrollLeft: 0,
                currentZoomStore,
                smoothScroll,
            });
        }
       // console.log("scrollleft does not ewqual 0" , {displayDate}, 'view display date', view.displayDate);
        view.displayDate = displayDate ?? view.displayDate;
        view.currentZoomStore = currentZoomStore;

        // Skip recalculation if zoom level and display date haven't changed
        const needsRecalc =
            view.currentZoomStore.scrollLevel !== currentZoomStore.scrollLevel ||
            displayDate.getTime() !== view.currentZoomStore.blocks?.[0]?.start.getTime();

        if (needsRecalc) {
            const newCurrentZoomStore = getZoomWithBlocks(
                view.currentZoomStore.scrollLevel,
                zoomRanges,
                displayDate,
                totalPxToDisplay,
            );
            if (newCurrentZoomStore == null) {
                console.error({
                    displayDate,
                    scrollLeft,
                    currentZoomStore,
                });
                throw new Error("getZoomWithBlocks returned null unexpectedly");
            }
            view.currentZoomStore = newCurrentZoomStore;
        }

        const startMs = view.currentZoomStore.blocks[0].start.getTime();
        const diffMs = displayDate.getTime() - startMs;

        const targetScrollLeft = diffMs * (view.currentZoomStore.cellPx / view.currentZoomStore.zoomRangePreset.cellMs - (scrollLeft ?? 0));

        if (smoothScroll) {
            contentDiv.scrollTo({
                left: targetScrollLeft,
                behavior: "smooth",
            })
        } else {
            contentDiv.scrollLeft = targetScrollLeft;
        }

        // If the new displaying date range extends the fetched jobs date range then we need to fetch more jobs
        const blocks = view.currentZoomStore.blocks;
        const displayStart = blocks[0].start;
        if (fetchedJobsRange.start > displayStart) {
            const prevStart = new Date(fetchedJobsRange.start);
            fetchedJobsRange.start.setTime(displayStart.getTime());
            fetchedJobsRange.start.setMonth(
                fetchedJobsRange.start.getMonth(),
            );
            if (refetchJobs) {
                debouncedFetchScheduleable({
                    csrfToken,
                    loading,
                    vehicleAssignmentMap,
                    siteVisitMap,
                    start: fetchedJobsRange.start,
                    end: prevStart,
                    vehicles,
                    relatedJobsMap,
                });
            }

        }

        const displayEnd = blocks[blocks.length - 1].end;

        if (fetchedJobsRange.end < displayEnd) {
            let newStart = new SvelteDate(fetchedJobsRange.end);
            fetchedJobsRange.end.setMonth(fetchedJobsRange.end.getMonth());
            if (refetchJobs) {
                debouncedFetchScheduleable({
                    csrfToken,
                    loading,
                    vehicleAssignmentMap,
                    siteVisitMap,
                    start: newStart,
                    end: fetchedJobsRange.end,
                    vehicles,
                    relatedJobsMap,
                });
            }
        }
    };
    const updateContainerHeight = () => {
        if (!topContainerNode) return;
        if (isFullscreen) {
            topContainerNode.style.height = '';
            return;
        }
        const top = topContainerNode.getBoundingClientRect().top;
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const height = Math.max(viewportHeight - top - 16, 300);
        topContainerNode.style.height = `${height}px`;
    };

    onMount(async () => {
        updateContainerHeight();
        window.visualViewport?.addEventListener('resize', updateContainerHeight);
        window.addEventListener('resize', updateContainerHeight);
        document.addEventListener('fullscreenchange', onFullscreenChange);

        setView({scrollLeft: 500, displayDate: displayDate});
        setViewMode('week');

        // Set the search string to the highlighted assignment ID if provided
        if (highlightedAssignmentId) {
            pageState.searchStr = highlightedAssignmentId;

            // Wait for DOM to update, then scroll to the highlighted element
            // Use retry logic since data may still be loading
            const scrollToHighlightedElement = (attempt = 1) => {
                const highlightedElement = document.querySelector(`[data-job-id="${highlightedAssignmentId}"]`);

                if (highlightedElement && contentDiv) {
                    const jobWrapper = highlightedElement.closest('.job-wrapper') as HTMLElement;
                    if (jobWrapper) {
                        const topMatch = jobWrapper.style.top?.match(/(\d+)/);
                        if (topMatch) {
                            const elementTop = parseInt(topMatch[1], 10);
                            const containerHeight = contentDiv.clientHeight;
                            const scrollTarget = Math.max(0, elementTop - (containerHeight / 2) + 40);
                            contentDiv.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                            return;
                        }
                    }
                }

                // Retry up to 10 times with increasing delay
                if (attempt < 10) {
                    setTimeout(() => scrollToHighlightedElement(attempt + 1), 300 * attempt);
                }
            };

            await tick();
            setTimeout(() => scrollToHighlightedElement(1), 500);
        }

        // Start background polling. The tick function bails out cleanly when
        // the user has unsaved changes, is mid-drag, or the tab is hidden, so
        // no work is wasted and unsaved edits are never clobbered.
        pollIntervalId = setInterval(pollForNewEvents, POLL_INTERVAL_MS);
    });

    const pollForNewEvents = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (modifiedCount > 0) return;
        if (loading.includes('fetchJobs')) return;
        if (isStateJob(pageState) && pageState.selected?.dragging) return;

        fetchScheduleable({
            csrfToken,
            loading,
            vehicleAssignmentMap,
            siteVisitMap,
            start: fetchedJobsRange.start,
            end: fetchedJobsRange.end,
            vehicles,
            relatedJobsMap,
        });
    };

    let currentFilteredVehicles: SchedularVehicle[] = $state(vehicles);
    let currentFilteredOverlappingJobs: OverlappingJobs = $state(overlappingJobs);

    const onMouseMove = (e: MouseEvent) => {
        if (isStateJob(pageState)) {
            return onMouseMoveJob(
                e,
                view.currentZoomStore,
                pageState,
                currentFilteredVehicles,
                dragTargetDiv,
                contentDiv,
                usersWidth,
                rowStepPx,
                view,
                currentFilteredOverlappingJobs,
                vehicleAssignmentMap,
                employeesMap,
                overlappingEmployeeAssignments,
                siteVisitMap,
            );
        }
    };

    const onMouseUp = (e: MouseEvent) => {
        // Skip deselection when a floating dropdown (Select, Popover, etc.) is open
        if (document.querySelector('[data-bits-floating-content-wrapper]')) {
            return;
        }
        if (isStateJob(pageState)) {
            return onMouseUpJob(
                e,
                pageState,
                baseUrl,
                csrfToken,
                vehicleAssignmentMap,
                employeesMap,
                overlappingEmployeeAssignments,
                siteVisitMap
            );
        }
    };
    const onMouseDownPos = (e: MouseEvent, dragPosRect: DOMRect) => {
        if (isStateJob(pageState)) {
            if (pageState.selected?.item.start === null) {
                // If the job is unscheduled then we want to center the view on the job start date
                // Use the job's actual start_date_time, not the assignment's start (which is null)
                const jobStartDateTime = pageState.selected?.item?.job?.start_date_time;
                if (!jobStartDateTime) return;
                let newDisplayDate = new SvelteDate(jobStartDateTime);
                newDisplayDate.setHours(0, 0, 0, 0);
                newDisplayDate.setDate(newDisplayDate.getDate() - 1);
                setView({displayDate: newDisplayDate, refetchJobs: false, smoothScroll: true});

            }


            return onMouseDownPosJob(
                e,
                dragPosRect,
                pageState,
                view,
                csrfToken,
                baseUrl,
                vehicleAssignmentMap,
                employeesMap,
                overlappingEmployeeAssignments,
                siteVisitMap
            );
        }
        throw new Error(
            "called onMouseDownPos without the correct selected type for page state",
        );
    };

    const onMouseDownLeftDur = (e: MouseEvent, boundingRect: DOMRect) => {
        if (isStateScheduledJob(pageState)) {
            return onMouseDownLeftDurJob(
                e,
                boundingRect,
                pageState,
                view,
            );
        }
        throw new Error(
            "called onMouseDownLeftDur without the correct selected type for page state",
        );
    };
    const onMouseDownRightDur = (
        e: MouseEvent,
        startDragDur: number,
        boundingRect: DOMRect,
    ) => {
        if (isStateScheduledJob(pageState)) {
            return onMouseDownRightDurJob(
                e,
                startDragDur,
                boundingRect,
                pageState,
                view,
            );
        }
        throw new Error(
            "called onMouseDownRightDur without the correct selected type for page state",
        );
    };
    const touchToMouseEvent = (e: TouchEvent): MouseEvent | null => {
        const touch = e.touches[0] ?? e.changedTouches[0];
        if (!touch) return null;
        return {
            clientX: touch.clientX,
            clientY: touch.clientY,
            x: touch.clientX,
            y: touch.clientY,
            buttons: 1,
            preventDefault: () => e.preventDefault(),
        } as unknown as MouseEvent;
    };

    const onTouchMove = (e: TouchEvent) => {
        const fakeEvent = touchToMouseEvent(e);
        if (!fakeEvent) return;
        if (isStateJob(pageState) && pageState.selected?.dragging != null) {
            e.preventDefault();
        }
        onMouseMove(fakeEvent);
    };

    const onTouchEnd = (e: TouchEvent) => {
        const fakeEvent = touchToMouseEvent(e);
        if (!fakeEvent) return;
        onMouseUp(fakeEvent);
    };

    let employeesContainer = $state<HTMLDivElement>();
    let assignEmployeesContainer = $state<HTMLDivElement>();
    let topContainerNode: HTMLDivElement = $state();

    let employees = $derived.by(() => {
        return Array.from(employeesMap.values());
    });

    // Autosave state
    let autosaveEnabled = $state(false);
    let autosaveStatus: AutosaveStatus = $state('idle');
    let isSaving = $state(false);

    // Navigation warning state
    let showNavigationWarning = $state(false);
    let pendingNavigationCallback: (() => void) | null = $state(null);
    let isNavigatingAway = $state(false);
    const pageLoadedAt = Date.now();
    const NAV_WARNING_DELAY_MS = 5000;

    // Create debounced autosave instance
    const debouncedAutosave = createDebouncedAutosave(2500);

    // Count of items that couldn't be autosaved due to validation
    let skippedAutosaveCount = $derived.by(() => {
        if (!autosaveEnabled) return 0;
        const invalidIds = getInvalidIds(validationStore);
        return invalidIds.size;
    });

    // Autosave effect - triggers when modifiedCount changes
    $effect(() => {
        // Dependencies
        const count = modifiedCount;
        const enabled = autosaveEnabled;
        const isDragging = isStateJob(pageState) && pageState.selected?.dragging;
        const hasError = autosaveStatus === 'error';

        // Skip if disabled, no modifications, currently dragging, or in error state (wait for retry)
        if (!enabled || count === 0 || isDragging || isSaving || hasError) {
            if (!enabled) {
                debouncedAutosave.cancel();
                autosaveStatus = 'idle';
            }
            return;
        }

        autosaveStatus = 'pending';

        debouncedAutosave.trigger(async () => {
            if (isSaving) return;
            isSaving = true;

            const result = await performAutosave({
                baseUrl,
                csrfToken,
                vehicleAssignments: vehicleAssignmentMap,
                siteVisits: siteVisitMap,
                validationStore,
                onStatusChange: (status) => {
                    autosaveStatus = status;
                },
            });

            isSaving = false;

            if (result.success && result.savedCount > 0) {
                // Reset to idle after showing saved status briefly
                setTimeout(() => {
                    if (autosaveStatus === 'saved') {
                        autosaveStatus = 'idle';
                    }
                }, 2000);
            } else if (!result.success && result.error) {
                // Show error in error overlay
                showServerErrorOverlay(
                    `Autosave failed: ${result.error}`,
                    { url: '/web-api/schedule', method: 'POST' }
                );
            }
        });

        // Cleanup on unmount or re-run
        return () => {
            debouncedAutosave.cancel();
        };
    });

    // Browser beforeunload warning for unsaved changes
    $effect(() => {
        const hasUnsavedChanges = modifiedCount > 0;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges && Date.now() - pageLoadedAt >= NAV_WARNING_DELAY_MS) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        if (hasUnsavedChanges) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });

    // Inertia navigation interception for unsaved changes
    $effect(() => {
        const hasUnsavedChanges = modifiedCount > 0;

        if (!hasUnsavedChanges) return;

        const removeListener = router.on('before', (event) => {
            if (isNavigatingAway) return true;

            // Don't warn if user hasn't been on page long enough
            if (Date.now() - pageLoadedAt < NAV_WARNING_DELAY_MS) return true;

            // Only intercept GET requests — form submissions (POST/PUT/DELETE) are not navigation
            const method = event.detail.visit.method?.toUpperCase() ?? 'GET';
            if (method !== 'GET') return true;

            // Ignore prefetch requests (triggered by hovering nav links)
            if (event.detail.visit.prefetch) return true;

            const targetUrl = event.detail.visit.url.toString();

            // Ignore internal/background requests (Boost, partial reloads, etc.)
            if (targetUrl.includes('_boost/') || targetUrl.includes('/_ignition/')) {
                return true;
            }

            // Ignore partial reloads (same page data refreshes)
            if (event.detail.visit.only && event.detail.visit.only.length > 0) {
                return true;
            }

            pendingNavigationCallback = () => {
                isNavigatingAway = true;
                router.visit(targetUrl);
            };
            showNavigationWarning = true;
            return false;
        });

        return () => {
            removeListener();
        };
    });

    // Cleanup on component destroy
    onDestroy(() => {
        debouncedAutosave.cancel();
        isNavigatingAway = false;
        if (pollIntervalId !== null) {
            clearInterval(pollIntervalId);
            pollIntervalId = null;
        }
        window.visualViewport?.removeEventListener('resize', updateContainerHeight);
        window.removeEventListener('resize', updateContainerHeight);
        document.removeEventListener('fullscreenchange', onFullscreenChange);
    });

</script>

{#if displayValidation}
    <Modal
        title="Save Scheduler Changes"
        onClose={() => (displayValidation = false)}
    >
        <Validation
            scrollToDate={setView}
            {view}
            {vehicleAssignmentMap}
            bind:isDisplaying={displayValidation}
            {modifiedCount}
            {pageState}
            {validationStore}
            {overlappingEmployeeAssignments}
        />

        <div class="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button
                variant="outline"
                onclick={() => (displayValidation = false)}
            >
                Close
            </Button>
            {#if modifiedCount > 0 && validationStore.missingJobType.length === 0 && validationStore.missingPurpose.length === 0 && validationStore.duplicateExclusiveType.length === 0}
                <Button
                    onclick={() => {
                        saveSvelte({
                            loading,
                            baseUrl,
                            csrfToken,
                            vehicleAssignments: vehicleAssignmentMap,
                            vehicles,
                            relatedJobsMap: relatedJobsMap,
                            siteVisits: siteVisitMap,
                        });
                        displayValidation = false;
                    }}
                >
                    Save Changes
                </Button>
            {/if}
        </div>
    </Modal>
{/if}
{#if showNavigationWarning}
    <AlertDialog.Root bind:open={showNavigationWarning}>
        <AlertDialog.Content>
            <AlertDialog.Header>
                <AlertDialog.Title>Unsaved Changes</AlertDialog.Title>
                <AlertDialog.Description>
                    You have {modifiedCount} unsaved change{modifiedCount !== 1 ? 's' : ''}.
                    What would you like to do?
                </AlertDialog.Description>
            </AlertDialog.Header>
            <AlertDialog.Footer class="flex gap-2 sm:flex-row">
                <AlertDialog.Cancel onclick={() => {
                    showNavigationWarning = false;
                    pendingNavigationCallback = null;
                }}>
                    Stay on Page
                </AlertDialog.Cancel>
                <Button variant="outline" onclick={() => {
                    showNavigationWarning = false;
                    if (pendingNavigationCallback) pendingNavigationCallback();
                }}>
                    Discard Changes
                </Button>
                <AlertDialog.Action onclick={async () => {
                    await saveSvelte({
                        loading,
                        baseUrl,
                        csrfToken,
                        vehicleAssignments: vehicleAssignmentMap,
                        vehicles,
                        relatedJobsMap,
                        siteVisits: siteVisitMap,
                    });
                    showNavigationWarning = false;
                    if (pendingNavigationCallback) pendingNavigationCallback();
                }}>
                    Save & Leave
                </AlertDialog.Action>
            </AlertDialog.Footer>
        </AlertDialog.Content>
    </AlertDialog.Root>
{/if}
{#if loading.length}
    <Loading/>
{/if}
<svelte:document
    on:mousemove={(e) => onMouseMove(e)}
    on:mouseup={(e) => onMouseUp(e)}
    on:touchmove|nonpassive={(e) => onTouchMove(e)}
    on:touchend={(e) => onTouchEnd(e)}
    on:keydown={(e) => { if (e.key === "Escape" && isFullscreen) isFullscreen = false; }}
/>
<div class="wrapper" class:fullscreen={isFullscreen} bind:this={topNode}>
    <div class="top" bind:this={topContainerNode}>
        <div class="selected-wrapper rounded"
             style:--display={`${selectedVisibility}`}
             style:--opacity={`${selectedOpacity}`}
        >
            <SelectedInfo
                {pageState}
                {validationStore}
                {vehicleAssignmentMap}
                {view}
                {setView}
                {vehicles}
                {relatedJobsMap}
                {jobTypeField}
                {purposeField}
                {employeesMap}
                bind:assignEmployeesContainer={assignEmployeesContainer}
            ></SelectedInfo>
        </div>
        <div class="scheduler-toolbar" data-dont-unselect="true">
            <SchedulerLegend />
            <SchedulerToolbar
                {readonlyPlanner}
                {modifiedCount}
                onSaveClick={() => { displayValidation = true; }}
                bind:autosaveEnabled
                {autosaveStatus}
                {skippedAutosaveCount}
            />
            <div class="job-search">
                <SearchField bind:value={pageState.searchStr} />
            </div>
            <div class="zoom-controls">
                    <div class="flex items-center gap-1">
                        <Button variant={viewMode === 'day' ? 'default' : 'outline'} size="sm" class="h-7 text-xs" onclick={() => setViewMode('day')}>Day</Button>
                        <Button variant={viewMode === 'week' ? 'default' : 'outline'} size="sm" class="h-7 text-xs" onclick={() => setViewMode('week')}>Week</Button>
                        <Button variant={viewMode === 'month' ? 'default' : 'outline'} size="sm" class="h-7 text-xs" onclick={() => setViewMode('month')}>Month</Button>
                    </div>
                    <div class="nav-buttons">
                        <Button variant="outline" size="icon" class="h-7 w-7" onclick={() => navStep(-1, true)}>
                            <ChevronsLeft class="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="icon" class="h-7 w-7" onclick={() => navStep(-1, false)}>
                            <ChevronLeft class="h-3.5 w-3.5" />
                        </Button>
                        <SchedulerDateField startDate={view.displayDate} {setView} flexDirCol={false} {viewMode} displayLabel={dateRangeLabel} />
                        <Button variant="outline" size="sm" class="h-7 text-xs" onclick={goToNow}>Now</Button>
                        <Button variant="outline" size="icon" class="h-7 w-7" onclick={() => navStep(1, false)}>
                            <ChevronRight class="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="icon" class="h-7 w-7" onclick={() => navStep(1, true)}>
                            <ChevronsRight class="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="icon" class="h-7 w-7" onclick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                            {#if isFullscreen}
                                <Minimize class="h-3.5 w-3.5" />
                            {:else}
                                <Maximize class="h-3.5 w-3.5" />
                            {/if}
                        </Button>
                    </div>
                </div>
            </div>
            <Scheduler
                {setView}
                {view}
                vehicleAssignmentsStore={vehicleAssignmentMap}
                siteVisitsStore={siteVisitMap}
                {overlappingJobs}
                bind:vehicles
                bind:dragTargetDiv
                bind:contentDiv
                {usersWidth}
                {rowStepPx}
                {pageState}
                {validationStore}
                {onMouseDownPos}
                {onMouseDownLeftDur}
                {onMouseDownRightDur}
                {totalPxToDisplay}
                {relatedJobsMap}
                {viewMode}
                onJobDoubleClick={() => { selectedInfoVisible = true; }}
                bind:filteredVehiclesOut={currentFilteredVehicles}
                bind:filteredOverlappingJobsOut={currentFilteredOverlappingJobs}
            />
        {#if isStateJob(pageState) && pageState.selected?.item?.start !== null}
            <div class="employees-wrapper">
                <div class="jobs-header">
                    <h2 class="text-center my-3">
                        Available Employees
                    </h2>
                    {#if employees.length > 0}
                        <div id="employeesContainer" class="employeesContainer" bind:this={employeesContainer}>
                            {#each employees as employee(employee.id)}
                                <EmployeeTile
                                    {employee}
                                    {view}
                                    {pageState}
                                    {assignEmployeesContainer}
                                    {employeesContainer}
                                    {employeesMap}
                                />
                            {/each}
                        </div>
                    {:else}
                        <p class="text-center text-gray-500">No employees available</p>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
</div>
<!--<Debug {jobsMap} />-->
<!-- <pre style="white-space: pre">{JSON.stringify(pageState, null, 4)}</pre> -->
<!-- <pre style="white-space: pre">{JSON.stringify(view, null, 4)}</pre> -->
<!--<pre style="white-space: pre">{JSON.stringify(-->
<!--        [...jobsMap.values()],-->
<!--        null,-->
<!--        4,-->
<!--    )}</pre>-->

<style lang="scss">
    .wrapper {
        height: 90vh;
        height: 90dvh;
        flex-direction: column;
        justify-content: center;

        &.fullscreen {
            position: fixed;
            inset: 0;
            height: 100vh;
            width: 100vw;
            z-index: 50;
            background-color: var(--background);

            .top {
                height: 100vh;
                width: 100vw;
                border-radius: 0;
                border: none;
                box-shadow: none;
            }
        }
    }

    .top {
        position: relative;
        display: grid;
        grid-template-columns: 1fr max-content;
        grid-template-rows: max-content 1fr;
        height: calc(100vh - 8rem);
        height: calc(100dvh - 8rem);
        width: 95vw;
        margin: auto;
        overflow: auto;
        box-shadow: 0 1px 3px 0 #9ca3af;
        border: 1px solid var(--border);
        border-radius: 1rem;
        gap: 1rem;
    }

    /* Toolbar spans the full width of the grid so it sits across the top
       even when the Available Employees panel takes column 2. */
    .top > .scheduler-toolbar {
        grid-column: 1 / -1;
    }

    .scheduler-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.5rem;
        padding: 0.25rem 0.5rem;
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
        overflow: visible;
        position: relative;
        z-index: 10;
    }

    .zoom-controls {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-shrink: 0;
    }

    .job-search {
        flex: 1 1 200px;
        min-width: 160px;
        max-width: 320px;
    }

    .nav-buttons {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    .row {
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    .users {
        gap: 0.5rem 0;
        position: relative;
        max-width: 20vw;
    }

    .categories-filter {
        position: absolute;
        right: -100%;
        width: 100%;
        background: var(--muted);
        border: solid 1px var(--border);
    }

    .selected-wrapper {
        display: var(--display);
        opacity: var(--opacity);
        transition: all 500ms;
        position: absolute;
        top: 50%;
        transform: translateY(-42%);
        right: calc(300px + 1rem);
        width: min(600px, calc(100% - 270px - 3rem));
        height: 650px;
        padding: 0;
        overflow-y: auto;
        z-index: 20;
        background-color: var(--card);
        box-shadow: 0 1px 3px 0 hsl(0 0% 0% / 0.3);
    }

    .jobs-wrapper,
    .employees-wrapper {
        display: grid;
        grid-template-rows: max-content 1fr;
        overflow: auto;
        padding: 0.5rem 1rem;
        width: 270px;
        flex-shrink: 0;
    }

    .employeesContainer,
    .jobs {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        transition: background 200ms;
        overflow: auto;

        &.dragging-unscheduling {
            background: hsl(var(--clr-primary-5), 0.2);
            color: hsl(var(--clr-on-error-1));
        }
    }

    .todo {
        .ticked {
            text-decoration: line-through;
        }
    }

</style>


